// FormFrame/fields/FieldSelect.tsx
import * as React from "react"
import { Label } from "@workspace/ui/components/shadcn/label"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@workspace/ui/components/shadcn/select"
import type { FieldDef, FormApi, FieldOption } from "./fieldTypes"
import { normalizeString, getRequiredMark, getHelpText, getErrorText } from "./fieldUtils"
import { cn } from "@workspace/ui/lib/utils"

type Props = {
    def: Extract<FieldDef, { type: "select" }>
    form: FormApi
    invalid: boolean
}

export function FieldSelect({ def, form, invalid }: Props) {
    const name = def.name
    const r = form.register(name)
    const value = normalizeString(form.getValue(name))

    const IconStart = (def as any).iconStart
    const IconEnd = (def as any).iconEnd

    const requiredMark = getRequiredMark(def)
    const helpText = getHelpText(def)
    const err = (form.formState.errors as any)?.[name]
    const errorText = getErrorText(err)

    return (
        <>
            <div className="ui-field-header">
                {def.label ? (
                    <Label htmlFor={name} className="ui-field-label">
                        {def.label}
                        {requiredMark}
                    </Label>
                ) : (
                    <span />
                )}
                {helpText ? (
                    <span className="ui-field-helptext">{helpText}</span>
                ) : (
                    <span />
                )}
            </div>

            <div className="relative w-full">
                {IconStart ? <IconStart className="ui-field-icon-start" /> : null}

                {/* Wrapper übernimmt Border / Error / Focus */}
                <div
                    className={cn(
                        "ui-control h-9 w-full",
                        invalid && "ui-control-error",
                        !invalid &&
                        "focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/30"
                    )}
                >
                    <Select
                        disabled={def.disabled}
                        value={value}
                        onValueChange={(v) => r.onChange(v)}
                    >
                        <SelectTrigger
                            className={cn(
                                "h-full w-full border-0 shadow-none bg-transparent",
                                IconStart && "pl-10",
                                IconEnd && "pr-10"
                            )}
                            onBlur={() => r.onBlur()}
                        >
                            <SelectValue
                                placeholder={def.placeholder || "Bitte wählen"}
                            />
                        </SelectTrigger>

                        <SelectContent>
                            {def.options.map((opt: FieldOption) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {IconEnd ? <IconEnd className="ui-field-icon-end" /> : null}
            </div>

            <div className="ui-field-error">
                {errorText || null}
            </div>
        </>
    )
}
